"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { isUsableWebSession, useAuthStore } from "@/lib/auth-store";
import { authHrefFor } from "@/lib/navigation";
import { postsApi } from "@/lib/client-api";
import { useMounted } from "@/lib/use-mounted";

const MAX_IMAGES = 4;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
const MAX_VIDEO_BYTES = 50 * 1024 * 1024;

type ComposerMode = "update" | "photo" | "video";

export function HomePostComposer() {
  const mounted = useMounted();
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const demoMode = useAuthStore((state) => state.demoMode);
  const hasSession = mounted ? isUsableWebSession({ user, accessToken, demoMode }) : false;
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [video, setVideo] = useState<File | null>(null);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const urls = images.map((file) => URL.createObjectURL(file));
    setImagePreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [images]);

  useEffect(() => {
    if (!video) {
      setVideoPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(video);
    setVideoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [video]);

  if (!mounted) {
    return (
      <section className="home-post-composer" aria-label="Create a post">
        <div className="home-post-composer-bar">
          <div className="home-post-composer-avatar">S</div>
          <span className="home-post-composer-trigger">Share an update with the community...</span>
        </div>
      </section>
    );
  }

  if (!hasSession) {
    return (
      <section id="create-post" className="home-post-composer" aria-label="Create a post">
        <div className="home-post-composer-bar">
          <div className="home-post-composer-avatar">S</div>
          <span className="home-post-composer-trigger">Sign in to share an update, photo, or video.</span>
          <Link href={authHrefFor("/", "Post an update")} className="btn btn-primary btn-small">
            Sign in to post
          </Link>
        </div>
      </section>
    );
  }

  const displayName = user?.displayName?.trim() || user?.email || "Skylight User";
  const avatarLabel = initials(displayName);

  const openComposer = (mode: ComposerMode) => {
    setExpanded(true);
    setError("");
    setSuccess("");
    window.setTimeout(() => {
      if (mode === "photo") imageInputRef.current?.click();
      if (mode === "video") videoInputRef.current?.click();
    }, 0);
  };

  const handleImages = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!selected.length) return;
    const invalid = selected.find(
      (file) => !["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > MAX_IMAGE_BYTES,
    );
    if (invalid) {
      setError(`${invalid.name} is not a supported image or is larger than 8 MB.`);
      return;
    }
    setError("");
    setSuccess("");
    setVideo(null);
    setImages(selected.slice(0, MAX_IMAGES));
  };

  const handleVideo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!selected) return;
    if (!["video/mp4", "video/quicktime"].includes(selected.type) || selected.size > MAX_VIDEO_BYTES) {
      setError("Choose an MP4 or MOV video that is 50 MB or smaller.");
      return;
    }
    setError("");
    setSuccess("");
    setImages([]);
    setVideo(selected);
  };

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || demoMode) return;
    const trimmedText = text.trim();
    if (!trimmedText && !images.length && !video) {
      setError("Write something or attach a photo or video before posting.");
      return;
    }

    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const dto: Parameters<typeof postsApi.create>[0] = {
        text: trimmedText || undefined,
        hashtags: parseHashtags(hashtags),
      };

      if (images.length) {
        setStatus("Uploading photos...");
        const uploads = await Promise.all(images.map((file) => postsApi.uploadMedia(file)));
        dto.imageUrls = uploads.map((upload) => upload.url);
      }

      if (video) {
        setStatus("Uploading video...");
        const upload = await postsApi.createVideoUploadUrl({ filename: video.name });
        if (upload.uploadUrl === "DEV_SKIP") {
          await postsApi.devVideoUpload(upload.cloudflareUid, video);
        } else {
          const response = await fetch(upload.uploadUrl, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/offset+octet-stream",
              "Upload-Offset": "0",
              "Tus-Resumable": "1.0.0",
            },
            body: video,
          });
          if (!response.ok) throw new Error("The video upload could not be completed.");
        }

        let readyJob: Awaited<ReturnType<typeof postsApi.mediaJobStatus>> | null = null;
        for (let attempt = 0; attempt < 60; attempt += 1) {
          setStatus(attempt < 3 ? "Processing video..." : `Processing video... ${attempt}s`);
          await wait(1000);
          const job = await postsApi.mediaJobStatus(upload.jobId);
          const jobStatus = job.status.toLowerCase();
          if (jobStatus === "ready") {
            readyJob = job;
            break;
          }
          if (jobStatus === "failed") {
            throw new Error(job.errorMessage || "Video processing failed.");
          }
        }
        if (!readyJob) throw new Error("Video processing is taking longer than expected. Please try again shortly.");
        const videoUrl = readyJob.videoUrl || readyJob.hlsUrl;
        if (!videoUrl) throw new Error("Video processing finished without a playback URL.");
        dto.videoUrl = videoUrl;
        dto.videoThumbnailUrl = readyJob.videoThumbnailUrl || undefined;
        dto.cloudflareVideoUid = readyJob.cloudflareUid;
      }

      setStatus("Publishing post...");
      await postsApi.create(dto);
      setText("");
      setHashtags("");
      setImages([]);
      setVideo(null);
      setExpanded(false);
      setStatus("");
      setSuccess("Your post is live.");
      router.refresh();
    } catch (submitError) {
      setStatus("");
      setError(formatError(submitError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="create-post" className="home-post-composer" aria-label="Create a post">
      <div className="home-post-composer-bar">
        <div className="home-post-composer-avatar">
          {user?.avatarUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={user.avatarUrl} alt="" />
          ) : (
            avatarLabel
          )}
        </div>
        <button type="button" className="home-post-composer-trigger" onClick={() => openComposer("update")}>
          Share an update with the community...
        </button>
        <div className="home-post-composer-actions">
          <button type="button" className="btn btn-ghost btn-small" onClick={() => openComposer("photo")}>
            Photo
          </button>
          <button type="button" className="btn btn-primary btn-small" onClick={() => openComposer("video")}>
            Video
          </button>
        </div>
      </div>

      {success ? <div className="notice notice-ok" style={{ marginTop: 12 }}>{success}</div> : null}

      {expanded ? (
        <form className="social-composer-panel" onSubmit={submit}>
          <div className="social-composer-header">
            <div>
              <strong>Create post</strong>
              <span>{displayName} - visible in the community feed</span>
            </div>
            <button type="button" className="social-composer-close" onClick={() => setExpanded(false)} disabled={busy}>
              Close
            </button>
          </div>

          <textarea
            className="textarea"
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="What is happening on your farm today?"
            maxLength={2000}
            disabled={busy}
          />
          <input
            className="input"
            value={hashtags}
            onChange={(event) => setHashtags(event.target.value)}
            placeholder="Hashtags, separated by commas (optional)"
            disabled={busy}
          />

          <div className="social-composer-tools">
            <button type="button" className="btn btn-ghost btn-small" onClick={() => imageInputRef.current?.click()} disabled={busy}>
              Add photos
            </button>
            <button type="button" className="btn btn-ghost btn-small" onClick={() => videoInputRef.current?.click()} disabled={busy}>
              Add video
            </button>
            {images.length ? <span className="social-composer-status">{images.length} photo{images.length === 1 ? "" : "s"} selected</span> : null}
            {video ? <span className="social-composer-status">{video.name}</span> : null}
          </div>

          <input ref={imageInputRef} className="social-composer-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleImages} />
          <input ref={videoInputRef} className="social-composer-file-input" type="file" accept="video/mp4,video/quicktime" onChange={handleVideo} />

          {imagePreviews.length ? (
            <div className="social-composer-media-preview" aria-label="Selected photos">
              {imagePreviews.map((preview, index) => (
                <figure key={preview}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={preview} alt={`Selected photo ${index + 1}`} />
                  <figcaption>{images[index]?.name}</figcaption>
                </figure>
              ))}
            </div>
          ) : null}

          {videoPreview ? (
            <div className="social-composer-media-preview" aria-label="Selected video">
              <figure>
                <video controls src={videoPreview} />
                <figcaption>{video?.name}</figcaption>
              </figure>
            </div>
          ) : null}

          {error ? <div className="notice notice-error">{error}</div> : null}
          <div className="social-composer-footer">
            <span className="social-composer-status">{busy ? status : `${text.length}/2000`}</span>
            <button className="btn btn-primary" type="submit" disabled={busy || demoMode}>
              {busy ? "Posting..." : "Post update"}
            </button>
          </div>
          {demoMode ? <div className="notice">Posting is unavailable in demo mode. Sign in with a real account to publish.</div> : null}
        </form>
      ) : null}
    </section>
  );
}

function parseHashtags(value: string) {
  return value
    .split(",")
    .map((tag) => tag.trim().replace(/^#/, ""))
    .filter(Boolean)
    .slice(0, 10);
}

function initials(value: string) {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "S";
}

function formatError(error: unknown) {
  if (error instanceof Error && error.message) return error.message;
  return "We could not publish that post. Please try again.";
}

function wait(milliseconds: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
}
